import { applyFontStyle } from "@/lib/styleService";

/**
 * Detects whether a paragraph is a template or document heading (e.g. "1.- Introduction",
 * "2.- Worldwide marketing authorisation status", "Abbreviations list").
 *
 * In Word documents, section titles and boundary headings carry Heading/Title styles
 * and/or are located at the start/end bookmark positions. These template headings must
 * NEVER be reformatted, recolored green, or changed to draft font during report adoption
 * or reparse.
 */
export function isTemplateHeadingParagraph(
  para: {
    style?: string;
    styleBuiltIn?: string;
    text?: string;
    font?: { bold?: boolean };
  },
  index?: number,
  total?: number
): boolean {
  const style = String((para as any)?.style || "");
  const styleBuiltIn = String((para as any)?.styleBuiltIn || "");

  // Any built-in or custom Heading/Title/Agency heading style
  if (/heading|title/i.test(style) || /heading|title/i.test(styleBuiltIn)) {
    return true;
  }

  // If at the boundary (containing _START or _END bookmark)
  const isBoundary = index !== undefined && total !== undefined && (index === 0 || index === total - 1);
  const text = (para?.text || "").trim();

  if (isBoundary && text.length > 0) {
    // Numbered section headings: "1.- Introduction", "1.1.", "1.1.1", "1 Introduction", "Section 1", "APPENDIX 1"
    if (/^(\d+(\.\d+)*[.-]?\s+|appendix\s+[0-9a-z.]+|section\s+\d+)/i.test(text)) {
      return true;
    }
    // Short bold titles at bookmark boundaries
    if (para?.font?.bold && text.length < 150) {
      return true;
    }
  }

  return false;
}

/**
 * Apply the add-in's "draft" (AI-generated) font style — green + italic —
 * to a range from non-hook code. The goal of download adoption is parity
 * with freshly generated reports: in the add-in's generation path, section
 * text starts in the draft look and only changes after the user reviews it.
 * A downloaded report must therefore read as unreviewed draft content
 * (defaultConfirmedStyles would flatten it to black upright text, which
 * looked like the styling was lost). `applyFontStyle` loads the document's
 * own style settings, so this stays in sync with user customization;
 * failures propagate to the caller, which treats styling as cosmetic and
 * continues adoption.
 */
export async function applyDraftStyleToRange(range: Word.Range): Promise<void> {
  await applyFontStyle("draftStyles", range);
}

